import { Injectable } from '@angular/core';
import { DashboardApiProviderService } from '@core/api';
import isEqual from 'lodash/isEqual';
import { catchError, Observable, of, tap } from 'rxjs';
import { ResponseErrorService } from '../errors/response-error.service';
import {
  TDashletCostComparisonResponse,
  TDashletLiveResponse,
  TDashletReportResponse,
  TDashletRequestData,
  TDashletStackedResponse,
  TDashletValueBreakdownRequestData,
  TDashletValueBreakdownResponseData,
} from './dashboard.consts';
import { TDashletReportRequestData } from './dashlet-report.service.consts';
import { EDashletType } from './dashlets-enums';

export type TDashletEventsParams = {
  type?: string;
  period?: string;
  accountId?: string;
  workspaceIds?: string[];
};

export type TDashletData = TDashletRequestData & {
  data: number;
};

@Injectable({
  providedIn: 'root',
})
export class DashboardDataFetchingService {
  private requestsQueue: TDashletReportRequestData[] = [];

  constructor(
    private responseErrorService: ResponseErrorService,
    private dashboardApiProviderService: DashboardApiProviderService,
  ) {}

  fetchCurrentTypeCount(body: TDashletRequestData): Observable<TDashletLiveResponse> {
    return this.handleRequest(
      this.findExistingRequest({
        type: body.type,
        period: body.period,
        workspaceIds: body.workspaceIds,
        accountId: body.accountId,
      }),
      () => this.dashboardApiProviderService.fetchCurrentTypeCount(body),
      body,
    );
  }

  fetchReports(body: TDashletRequestData): Observable<TDashletReportResponse> {
    return this.handleRequest(
      this.findExistingRequest({
        type: body.type,
        period: body.period,
        workspaceIds: body.workspaceIds,
        accountId: body.accountId,
      }),
      () => this.dashboardApiProviderService.fetchReports(body),
      body,
    );
  }

  fetchCostComparison({
    assetId = '',
    workspacesCosts = {},
  }: {
    assetId: string;
    workspacesCosts: { [workspaceId: string]: string[] };
  }): Observable<TDashletCostComparisonResponse> {
    return this.handleRequest(
      this.findExistingRequest({
        type: EDashletType.COST_COMPARISON,
        period: null,
        workspaceIds: null,
        accountId: null,
        assetId,
        workspacesCosts,
      }),
      () =>
        this.dashboardApiProviderService.fetchCostComparison({
          assetId,
          workspacesCosts,
        }),
      {
        type: EDashletType.COST_COMPARISON,
        period: null,
        accountId: null,
        workspaceIds: null,
        assetId,
        workspacesCosts,
      },
    );
  }

  fetchValueBreakdown(
    body: TDashletValueBreakdownRequestData,
  ): Observable<TDashletValueBreakdownResponseData> {
    return this.handleRequest(
      this.findExistingRequest({
        type: EDashletType.VALUE_BREAKDOWN,
        period: null,
        workspaceId: body.workspaceId,
        listCustomFieldId: body.listCustomFieldId,
        numericalCustomFieldId: body.numericalCustomFieldId,
        assetId: body.assetId,
      }),
      () => this.dashboardApiProviderService.fetchValueBreakdown(body),
      {
        type: EDashletType.VALUE_BREAKDOWN,
        period: null,
        accountId: null,
        assetId: body.assetId,
        workspacesCosts: null,
        workspaceId: body.workspaceId,
        numericalCustomFieldId: body.numericalCustomFieldId,
        listCustomFieldId: body.listCustomFieldId,
      },
    );
  }

  fetchStackedStatus({
    accountId = '',
    workspaceIds = [],
  }: {
    accountId?: string;
    workspaceIds?: string[];
  }): Observable<TDashletStackedResponse> {
    if (!workspaceIds?.length) {
      return of({});
    }

    return this.handleRequest(
      this.findExistingRequest({
        type: EDashletType.CURRENT_STACKED_STATUS,
        period: null,
        workspaceIds,
        accountId,
      }),
      () =>
        this.dashboardApiProviderService.fetchStackedStatus({
          ...(accountId ? { accountId } : {}),
          workspaceIds,
          type: EDashletType.CURRENT_STACKED_STATUS,
        }),
      {
        type: EDashletType.CURRENT_STACKED_STATUS,
        period: null,
        accountId,
        workspaceIds,
        assetId: null,
        workspacesCosts: null,
      },
    );
  }

  clearDashletData(): void {
    this.requestsQueue = [];
  }

  getRequestQueue(): TDashletReportRequestData[] {
    return this.requestsQueue;
  }

  private handleRequest<
    T extends
      | TDashletStackedResponse
      | TDashletReportResponse
      | TDashletCostComparisonResponse
      | TDashletLiveResponse
      | TDashletValueBreakdownResponseData,
  >(
    existingRequest: Partial<TDashletReportRequestData>,
    apiCall: () => Observable<T>,
    requestData: Partial<TDashletReportRequestData>,
  ): Observable<T> {
    if (existingRequest) {
      return existingRequest.request as Observable<T>;
    }

    const request = apiCall();
    this.requestsQueue.push({ ...requestData, request } as TDashletReportRequestData);

    return request.pipe(
      tap((response) => {
        const foundRequest = this.findExistingRequest({
          type: requestData.type,
          period: requestData.period,
          workspaceIds: requestData.workspaceIds,
          accountId: requestData.accountId,
          assetId: requestData.assetId,
          workspacesCosts: requestData.workspacesCosts,
          workspaceId: requestData.workspaceId,
          numericalCustomFieldId: requestData.numericalCustomFieldId,
          listCustomFieldId: requestData.listCustomFieldId,
        });
        foundRequest.data = response;
      }),
      catchError((error) =>
        this.responseErrorService.handleRequestError(error, 'Failed to fetch dashboard data'),
      ),
    );
  }

  private findExistingRequest({
    type,
    period,
    workspaceIds,
    accountId,
    assetId,
    workspacesCosts,
    workspaceId,
    numericalCustomFieldId,
    listCustomFieldId,
  }: {
    type: string;
    period?: string;
    workspaceIds?: string[];
    accountId?: string;
    assetId?: string;
    workspacesCosts?: { [workspaceId: string]: string[] };
    workspaceId?: string;
    numericalCustomFieldId?: string;
    listCustomFieldId?: string;
  }): TDashletReportRequestData {
    return this.requestsQueue.find((_dashlet) => {
      const typeMatch = _dashlet.type === type;
      const periodMatch = _dashlet.period === period;
      const workspaceIdsMatch =
        (!_dashlet.workspaceIds && !workspaceIds) ||
        (_dashlet.workspaceIds?.length === workspaceIds?.length &&
          _dashlet.workspaceIds?.every((dashletWorkspaceId) =>
            workspaceIds?.includes(dashletWorkspaceId),
          ));
      const accountIdMatch =
        _dashlet.accountId === accountId || (!_dashlet.accountId && !accountId);
      const assetIdMatch = _dashlet.assetId === assetId || (!_dashlet.assetId && !assetId);
      const workspaceCostsMatch =
        isEqual(_dashlet.workspacesCosts, workspacesCosts) ||
        (!_dashlet.workspacesCosts && !workspacesCosts);
      const workspaceIdMatch =
        _dashlet.workspaceId === workspaceId || (!_dashlet.workspaceId && !workspaceId);
      const numericalCustomFieldIdMatch =
        _dashlet.numericalCustomFieldId === numericalCustomFieldId ||
        (!_dashlet.numericalCustomFieldId && !numericalCustomFieldId);
      const listCustomFieldIdMatch =
        _dashlet.listCustomFieldId === listCustomFieldId ||
        (!_dashlet.listCustomFieldId && !listCustomFieldId);

      return (
        typeMatch &&
        periodMatch &&
        workspaceIdsMatch &&
        accountIdMatch &&
        assetIdMatch &&
        workspaceCostsMatch &&
        workspaceIdMatch &&
        numericalCustomFieldIdMatch &&
        listCustomFieldIdMatch
      );
    });
  }
}
